import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Copy, Link2, QrCode, CheckCircle, Users, Tag } from "lucide-react";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";

export default function OptIn() {
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [customMessage, setCustomMessage] = useState("Olá! Clique no link abaixo para se cadastrar e receber nossas comunicações pelo WhatsApp.");
  const [generatedLink, setGeneratedLink] = useState("");

  const { data: tags = [] } = trpc.tags.list.useQuery();

  const baseUrl = window.location.origin;

  const generateLink = () => {
    const params = new URLSearchParams();
    if (selectedTagIds.length > 0) params.set("tags", selectedTagIds.join(","));
    if (customMessage) params.set("msg", encodeURIComponent(customMessage));
    const link = `${baseUrl}/optin?${params.toString()}`;
    setGeneratedLink(link);
    toast.success("Link gerado com sucesso!");
  };

  const copyLink = () => {
    navigator.clipboard.writeText(generatedLink);
    toast.success("Link copiado para a área de transferência!");
  };

  const toggleTag = (tagId: number) => {
    setSelectedTagIds(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    );
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Opt-in via Link</h1>
          <p className="text-muted-foreground mt-1">
            Gere links de cadastro para que novos contatos se inscrevam diretamente na sua base.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Configuração do link */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Tags Automáticas
                </CardTitle>
                <CardDescription>
                  Contatos que se cadastrarem via este link receberão automaticamente as tags selecionadas.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {tags.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhuma tag cadastrada. Crie tags primeiro.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {tags.map(tag => (
                      <button
                        key={tag.id}
                        onClick={() => toggleTag(tag.id)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border-2 transition-all ${
                          selectedTagIds.includes(tag.id)
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border bg-background text-foreground hover:border-primary/50"
                        }`}
                      >
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: tag.color ?? "#22c55e" }}
                        />
                        {tag.name}
                        {selectedTagIds.includes(tag.id) && (
                          <CheckCircle className="w-3 h-3" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Mensagem de Boas-vindas</CardTitle>
                <CardDescription>
                  Texto exibido na página de cadastro para o novo contato.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <textarea
                  className="w-full min-h-[100px] p-3 rounded-md border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                  value={customMessage}
                  onChange={e => setCustomMessage(e.target.value)}
                  placeholder="Mensagem de boas-vindas..."
                />
              </CardContent>
            </Card>

            <Button onClick={generateLink} className="w-full gap-2">
              <Link2 className="w-4 h-4" />
              Gerar Link de Cadastro
            </Button>
          </div>

          {/* Link gerado */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link2 className="w-4 h-4" />
                  Link Gerado
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {generatedLink ? (
                  <>
                    <div className="flex gap-2">
                      <Input value={generatedLink} readOnly className="text-xs" />
                      <Button variant="outline" size="sm" onClick={copyLink}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="p-4 bg-muted rounded-lg text-sm space-y-2">
                      <p className="font-medium">Como usar:</p>
                      <ul className="space-y-1 text-muted-foreground list-disc list-inside">
                        <li>Compartilhe o link em e-mails, redes sociais ou WhatsApp</li>
                        <li>O contato preenche nome, telefone e empresa</li>
                        <li>Após o cadastro, as tags selecionadas são aplicadas automaticamente</li>
                        <li>As automações com gatilho "Novo Contato" são disparadas</li>
                      </ul>
                    </div>
                    {selectedTagIds.length > 0 && (
                      <div>
                        <p className="text-sm font-medium mb-2">Tags que serão aplicadas:</p>
                        <div className="flex flex-wrap gap-1">
                          {selectedTagIds.map(id => {
                            const tag = tags.find(t => t.id === id);
                            if (!tag) return null;
                            return (
                              <span
                                key={id}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white"
                                style={{ backgroundColor: tag.color ?? "#22c55e" }}
                              >
                                {tag.name}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Link2 className="w-12 h-12 mx-auto mb-3 opacity-20" />
                    <p>Configure as opções ao lado e clique em "Gerar Link"</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Como funciona
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-3">
                <div className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">1</span>
                  <p>Você gera um link personalizado com as tags desejadas</p>
                </div>
                <div className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">2</span>
                  <p>Compartilha o link com seu público-alvo (email, redes sociais, etc.)</p>
                </div>
                <div className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">3</span>
                  <p>O contato preenche o formulário e é cadastrado automaticamente</p>
                </div>
                <div className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">4</span>
                  <p>As automações de "Novo Contato" são disparadas imediatamente</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
